import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { clientApi } from '../services/api';
import { ArrowLeft, Play, Copy, Check, Trash2, Users, Video, Key, ExternalLink, Code } from 'lucide-react';

export default function Playground() {
  const [apiKey, setApiKey] = useState(localStorage.getItem('playground_apiKey') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState('');
  const [copied, setCopied] = useState('');
  const [roomName, setRoomName] = useState('Consulta de teste');
  const [maxParticipants, setMaxParticipants] = useState(2);
  const [joinName, setJoinName] = useState('Dr. Teste');
  const [joinRole, setJoinRole] = useState('host');
  const [selectedRoomId, setSelectedRoomId] = useState('');

  const saveApiKey = (key) => {
    setApiKey(key);
    localStorage.setItem('playground_apiKey', key);
  };

  const addResult = (action, data, status = 'success') => {
    setResults(prev => [{
      id: Date.now(),
      action,
      data,
      status,
      timestamp: new Date().toLocaleTimeString('pt-BR'),
    }, ...prev].slice(0, 20));
  };

  const runAction = async (action, fn) => {
    if (!apiKey.trim()) { alert('Insira a API Key primeiro'); return; }
    setLoading(action);
    try {
      const result = await fn();
      addResult(action, result);
      return result;
    } catch (err) {
      addResult(action, { error: err.message }, 'error');
    } finally {
      setLoading('');
    }
  };

  const handleCreateRoom = () => runAction('POST /api/rooms', async () => {
    const result = await clientApi.createRoom(apiKey, { displayName: roomName, maxParticipants });
    if (result.roomId) setSelectedRoomId(result.roomId);
    return result;
  });

  const handleListRooms = () => runAction('GET /api/rooms', () => clientApi.listRooms(apiKey));

  const handleGetRoom = () => {
    if (!selectedRoomId) { alert('Crie ou selecione uma sala primeiro'); return; }
    runAction(`GET /api/rooms/${selectedRoomId}`, () => clientApi.getRoom(apiKey, selectedRoomId));
  };

  const handleJoinRoom = () => {
    if (!selectedRoomId) { alert('Crie uma sala primeiro'); return; }
    runAction(`POST /api/rooms/${selectedRoomId}/join`, () =>
      clientApi.joinRoom(apiKey, selectedRoomId, joinName, joinRole));
  };

  const handleDeleteRoom = () => {
    if (!selectedRoomId) { alert('Selecione uma sala primeiro'); return; }
    runAction(`DELETE /api/rooms/${selectedRoomId}`, () => clientApi.deleteRoom(apiKey, selectedRoomId));
  };

  const handleOpenRoom = () => {
    if (!selectedRoomId) { alert('Crie uma sala primeiro'); return; }
    window.open(`/sala/${selectedRoomId}`, '_blank');
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="border-b border-gray-800 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Link to="/admin" className="text-gray-400 hover:text-white"><ArrowLeft size={20} /></Link>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Code size={20} /> API Playground
            </h1>
            <p className="text-sm text-gray-500">Teste a API como se fosse o cliente (Help, etc.)</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 flex gap-6">
        {/* Left: Controls */}
        <div className="w-96 flex-shrink-0 space-y-4">
          {/* API Key */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
              <Key size={14} /> API Key do Cliente
            </h3>
            <input value={apiKey} onChange={(e) => saveApiKey(e.target.value)}
              placeholder="hvc_abc123..."
              className="w-full bg-gray-800 rounded-lg px-3 py-2 text-sm font-mono text-emerald-400 outline-none focus:ring-2 focus:ring-indigo-500" />
            <p className="text-xs text-gray-600 mt-1">Copie do painel Admin ao criar cliente</p>
          </div>

          {/* Criar Sala */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
              <Video size={14} /> 1. Criar Sala
            </h3>
            <div className="space-y-2">
              <input value={roomName} onChange={(e) => setRoomName(e.target.value)}
                placeholder="Nome da sala" className="w-full bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none" />
              <div className="flex gap-2">
                <input type="number" value={maxParticipants} onChange={(e) => setMaxParticipants(parseInt(e.target.value) || 2)}
                  className="w-20 bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none" />
                <span className="text-gray-500 text-sm self-center">participantes</span>
              </div>
              <button onClick={handleCreateRoom} disabled={loading === 'POST /api/rooms'}
                className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                <Play size={14} /> {loading === 'POST /api/rooms' ? 'Criando...' : 'POST /api/rooms'}
              </button>
            </div>
          </div>

          {/* Sala selecionada */}
          {selectedRoomId && (
            <div className="bg-gray-900 border border-emerald-800/50 rounded-xl p-4">
              <h3 className="font-semibold mb-2 text-sm text-emerald-400">Sala ativa</h3>
              <div className="flex items-center gap-2 bg-gray-800 rounded-lg px-3 py-2 mb-3">
                <code className="flex-1 text-xs text-emerald-300 break-all">{selectedRoomId}</code>
                <button onClick={() => copyText(selectedRoomId, 'roomId')} className="text-gray-400 hover:text-white">
                  {copied === 'roomId' ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
              <div className="space-y-2">
                <button onClick={handleOpenRoom}
                  className="w-full py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm flex items-center justify-center gap-2">
                  <ExternalLink size={14} /> Abrir Sala no Navegador
                </button>
              </div>
            </div>
          )}

          {/* Acoes */}
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
              <Play size={14} /> 2. Acoes
            </h3>
            <div className="space-y-2">
              <button onClick={handleListRooms} disabled={!!loading}
                className="w-full py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                GET /api/rooms
              </button>
              <button onClick={handleGetRoom} disabled={!!loading || !selectedRoomId}
                className="w-full py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                GET /api/rooms/{'{roomId}'}
              </button>
              <div className="flex gap-2">
                <input value={joinName} onChange={(e) => setJoinName(e.target.value)}
                  placeholder="Nome" className="flex-1 bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none" />
                <select value={joinRole} onChange={(e) => setJoinRole(e.target.value)}
                  className="bg-gray-800 rounded-lg px-2 py-2 text-sm outline-none">
                  <option value="host">host</option>
                  <option value="participant">participant</option>
                </select>
              </div>
              <button onClick={handleJoinRoom} disabled={!!loading || !selectedRoomId}
                className="w-full py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                <Users size={14} /> POST /join (gerar token)
              </button>
              <button onClick={handleDeleteRoom} disabled={!!loading || !selectedRoomId}
                className="w-full py-2 bg-red-600/20 hover:bg-red-600/40 text-red-400 rounded-lg text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                <Trash2 size={14} /> DELETE /api/rooms/{'{roomId}'}
              </button>
            </div>
          </div>

          {/* Embed code */}
          {selectedRoomId && (
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <h3 className="font-semibold mb-2 text-sm">3. Codigo Embed (iframe)</h3>
              <div className="relative">
                <pre className="bg-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto">
{`<iframe
  src="${window.location.origin}/embed/${selectedRoomId}"
  width="100%"
  height="600"
  allow="camera; microphone"
  style="border:none; border-radius:12px"
></iframe>`}
                </pre>
                <button onClick={() => copyText(`<iframe src="${window.location.origin}/embed/${selectedRoomId}" width="100%" height="600" allow="camera; microphone" style="border:none; border-radius:12px"></iframe>`, 'embed')}
                  className="absolute top-2 right-2 text-gray-500 hover:text-white">
                  {copied === 'embed' ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right: Results */}
        <div className="flex-1">
          <div className="bg-gray-900 border border-gray-800 rounded-xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="font-semibold text-sm">Respostas da API</h3>
              {results.length > 0 && (
                <button onClick={() => setResults([])} className="text-xs text-gray-500 hover:text-white">
                  Limpar
                </button>
              )}
            </div>

            {results.length === 0 ? (
              <div className="p-12 text-center text-gray-600">
                <Code size={48} className="mx-auto mb-4 opacity-20" />
                <p>Execute uma acao para ver a resposta aqui</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-800 max-h-[75vh] overflow-y-auto">
                {results.map((r) => (
                  <div key={r.id} className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${r.status === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}></span>
                        <code className="text-sm font-medium text-indigo-400">{r.action}</code>
                      </div>
                      <span className="text-xs text-gray-600">{r.timestamp}</span>
                    </div>
                    <pre className="bg-gray-800 rounded-lg p-3 text-xs text-gray-300 overflow-x-auto max-h-64">
                      {JSON.stringify(r.data, null, 2)}
                    </pre>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
